import React, { useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { BarChart3, ChevronDown, ChevronUp, Loader2, TrendingUp } from 'lucide-react';
import { routinesAPI } from '../services/api';

// Lightweight inline SVG trend curve (no external chart dependency).
const TrendCurve = ({ points }) => {
  const W = 340, H = 110, PX = 8, PY = 12;
  const n = points.length;
  if (n < 2) return null;
  const x = (i) => PX + (i / (n - 1)) * (W - 2 * PX);
  const y = (v) => H - PY - (v / 100) * (H - 2 * PY);
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${line} L${x(n - 1).toFixed(1)},${(H - PY).toFixed(1)} L${x(0).toFixed(1)},${(H - PY).toFixed(1)} Z`;
  const avg = Math.round(points.reduce((a, p) => a + p.value, 0) / n);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-28" data-testid="routine-trend-curve" preserveAspectRatio="none">
      <defs>
        <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(249,115,22)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="rgb(249,115,22)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 50, 100].map((g) => (
        <line key={g} x1={PX} x2={W - PX} y1={y(g)} y2={y(g)} stroke="currentColor" className="text-muted-foreground/20" strokeWidth="1" strokeDasharray="3 3" />
      ))}
      <line x1={PX} x2={W - PX} y1={y(avg)} y2={y(avg)} stroke="rgb(56,189,248)" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.8" />
      <path d={area} fill="url(#trendFill)" />
      <path d={line} fill="none" stroke="rgb(249,115,22)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

export const RoutineStats = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { if (open && !data) routinesAPI.stats().then(setData).catch(() => {}); }, [open, data]);

  const trend = useMemo(() => {
    if (!data || !data.routines.length) return [];
    const nr = data.routines.length;
    return data.routines[0].daily.map((_, i) => {
      const done = data.routines.reduce((a, r) => a + (r.daily[i]?.done || 0), 0);
      return { date: data.routines[0].daily[i]?.date, value: Math.round((done / nr) * 100) };
    });
  }, [data]);

  const avg = trend.length ? Math.round(trend.reduce((a, p) => a + p.value, 0) / trend.length) : 0;

  return (
    <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50" data-testid="routine-stats">
      <button onClick={() => setOpen((o) => !o)} data-testid="routine-stats-toggle" className="w-full flex items-center justify-between">
        <span className="font-bold text-red-950 dark:text-red-50 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-orange-500" />Mes statistiques par routine</span>
        {open ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
      </button>

      {open && (
        <div className="mt-4">
          {!data ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>
          ) : (
            <>
              {/* Trend curve (daily regularity over the full period) */}
              <div className="mb-6" data-testid="routine-trend">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-foreground flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-orange-500" />Tendance de régularité</span>
                  <span className="text-xs text-muted-foreground">moyenne <span className="font-bold text-sky-500">{avg}%</span></span>
                </div>
                <TrendCurve points={trend} />
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1">
                  <span>il y a {data.days} j</span>
                  <span>aujourd'hui</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mb-4">Détail hebdomadaire par routine sur {data.days} jours — profil {data.profile === 'distributeur' ? 'Distributeur' : 'Client'}.</p>
              <div className="space-y-4">
                {data.routines.map((r) => {
                  const done = r.daily.reduce((a, d) => a + d.done, 0);
                  return (
                    <div key={r.key} data-testid={`stat-${r.key}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-semibold text-foreground">{r.label}</span>
                        <span className="text-xs text-muted-foreground">{done}/{data.days} j</span>
                      </div>
                      <div className="flex items-end gap-1 h-12">
                        {r.weekly.map((w, i) => {
                          const pct = w.total ? (w.count / w.total) * 100 : 0;
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${w.week}: ${w.count}/${w.total}`}>
                              <div className="w-full h-9 bg-muted rounded flex items-end overflow-hidden">
                                <div className="w-full bg-orange-500 rounded" style={{ height: `${Math.max(pct, 3)}%` }} />
                              </div>
                              <span className="text-[9px] text-muted-foreground">{w.week}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
};
